import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stepUpHeaders } from "../../gateway/api";
import { AFFILIATE_KINDS, AFFILIATE_PATHS, RATE_SOURCES } from "../../affiliate/contract";
import { useLanguage, useT } from "../../lib/LanguageContext";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { apiErrorCode } from "../../lib/apiErrors";
import { fmtDate, fmtBps, pctToBps, EMPTY } from "../../admin/affiliate/format";
import { useScreenWrites } from "../screenWrites";

const KIND_KEYS = { saas: "saff.kind.saas", marketplace: "saff.kind.marketplace" };

// MIRRORS `gateway.schemas.PRODUCT_TYPES`, minus `stored_value`. The same four the
// gateway's own commission-rule editor offers (`super_admin/GatewayConsole.jsx`), and they
// reuse that screen's `gw.productType.*` labels rather than a second translation of the
// same four words. `stored_value` is deliberately absent: a gift-card PURCHASE carries no
// platform commission at all (contract §10.1), so the marketplace base is zero and a rule
// written for it could never take a cut of anything.
const PRODUCT_TYPES = ["telehealth", "in_person", "digital", "physical"];
const ANY_PRODUCT_TYPE = "__any__";

const MAX_NOTE = 500;

/** The chip explaining WHERE a resolved rate came from — the platform default, or the
 *  operator-authored rule that beat it. `slot.ruleId` is non-null exactly when
 *  `slot.source === "rule"` (contract invariant), so the lookup into `rows` only runs then. */
function SourceChip({ slot, rows, lang, t, testId }) {
  if (slot.source === "default") {
    return (
      <span
        data-testid={testId}
        className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
      >
        {t("saff.rules.sourceDefault")}
      </span>
    );
  }
  const rule = rows?.find((r) => r.id === slot.ruleId);
  return (
    <span
      data-testid={testId}
      className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
    >
      {t("saff.rules.sourceRule", { who: rule?.setBy || EMPTY, date: fmtDate(rule?.effectiveFrom, lang) })}
    </span>
  );
}

/** One of the two kind-level slots (saas / marketplace, `productType: null`) — the rate an
 *  operator actually came here to read, stated large enough to answer "what's the rate?"
 *  without opening the history table. */
function InForceMainRow({ slot, label, baseText, rows, lang, t, rowTestId, chipTestId }) {
  return (
    <div data-testid={rowTestId} className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        <p className="text-sm text-slate-500 mt-0.5">{baseText}</p>
      </div>
      <div className="text-right shrink-0">
        <div className="text-2xl tabular-nums text-slate-900">
          {t("saff.rules.ratePct", { pct: fmtBps(slot.rateBps) })}
        </div>
        <div className="mt-1">
          <SourceChip slot={slot} rows={rows} lang={lang} t={t} testId={chipTestId} />
        </div>
      </div>
    </div>
  );
}

/** A per-product-type marketplace slot, shown ONLY when it diverges from the marketplace
 *  kind default — see the caller's filter. Compact: this is a footnote under the sales
 *  row, not a second headline rate. */
function InForceProductLine({ slot, rows, lang, t, rowTestId, chipTestId }) {
  return (
    <div data-testid={rowTestId} className="flex items-center justify-between text-sm text-slate-600 py-1">
      <span>{t(`gw.productType.${slot.productType}`)}</span>
      <span className="flex items-center gap-2">
        <span className="tabular-nums">{t("saff.rules.ratePct", { pct: fmtBps(slot.rateBps) })}</span>
        <SourceChip slot={slot} rows={rows} lang={lang} t={t} testId={chipTestId} />
      </span>
    </div>
  );
}

/**
 * ★ `POST /super-admin/affiliate/rules` — the effective-dated rate history, plus
 * `GET .../rules#inForce` — the resolved rate per slot RIGHT NOW.
 *
 * THE FORM SPEAKS PERCENT, THE WIRE SPEAKS BASIS POINTS, and the conversion is exact:
 * `12.5 %` is 1250 bps, never 12.5 of anything. Operators write percentages and the money
 * core stores integers, so exactly one place converts and it is here.
 *
 * WRITING A RULE IS CLOSE-THEN-INSERT, done by the server in one operation: the open rule
 * for the same `(kind, productType)` has its `effectiveTo` stamped with the new rule's
 * `effectiveFrom`, and a partial unique index makes overlapping windows impossible to
 * store. Nothing here edits or deletes a row — a rate that was in force is a historical
 * fact, and a commission written under it keeps the rate it was written with.
 *
 * A SAAS RULE CARRIES NO PRODUCT TYPE (a subscription has no product nature), which is why
 * the selector disappears rather than being sent as `null` behind the operator's back.
 *
 * THREE BLOCKS, one screen: what the rate IS today (`inForce`, read-only), how to CHANGE
 * it (the form below, step-up gated), and what it WAS (the history table). The first block
 * exists because "platform defaults apply" told an operator nothing about the number in
 * force — this reads it from the same `inForce` slot the accrual itself would resolve.
 */
export default function AffiliateRulesEditor({ gatewayApi }) {
  // Every affiliate write is finance.write behind a step-up (minted with finance.write too).
  const { manage: canManage } = useScreenWrites("affiliate");
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [rows, setRows] = useState(null);
  const [inForce, setInForce] = useState(null);
  const [kindFilter, setKindFilter] = useState("");
  const [draft, setDraft] = useState({
    kind: AFFILIATE_KINDS[1], productType: ANY_PRODUCT_TYPE, ratePct: "", note: "",
  });
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState("");

  // UNFILTERED, always: the history table's kind filter is a client-side view over the
  // same set the in-force chips read. Filtering the GET by `kind` would starve `SourceChip`
  // of exactly the row it needs whenever an operator has the history filtered to the
  // OTHER kind — the marketplace chip would look up a rule that is not in `rows` and
  // silently render EMPTY. The set is a handful of rows with no pagination in the
  // contract, so there is no cost to reading all of it once.
  const load = useCallback(async () => {
    const { data } = await api.get(AFFILIATE_PATHS.adminRules);
    if (!data || !Array.isArray(data.rows)) throw payloadShapeError();
    if (!Array.isArray(data.inForce)
        || data.inForce.some((r) => !Number.isInteger(r.rateBps) || !RATE_SOURCES.includes(r.source))) {
      throw payloadShapeError();
    }
    setRows(data.rows);
    setInForce(data.inForce);
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const bps = pctToBps(draft.ratePct);
  const rateValid = draft.ratePct !== "" && Number.isInteger(bps) && bps >= 0 && bps <= 10000;
  const noteValid = draft.note.length <= MAX_NOTE;
  const valid = rateValid && noteValid;

  const confirm = async ({ stepUpToken }) => {
    setRefusal("");
    try {
      await api.post(AFFILIATE_PATHS.adminRules, {
        kind: draft.kind,
        // `null` is a REAL value here, not an omission: it is the default rule for the
        // kind. A saas rule may carry nothing else.
        productType: draft.kind === "saas" || draft.productType === ANY_PRODUCT_TYPE
          ? null : draft.productType,
        rateBps: bps,
        note: draft.note,
      }, stepUpHeaders(stepUpToken));
      toast.success(t("saff.rules.added"));
      setDraft((d) => ({ ...d, ratePct: "", note: "" }));
      return { ok: true };
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "REAUTH_REQUIRED") return { ok: false, code };
      setRefusal(affiliateErrorText(e, { t, tv, prefix: "saff", fallback: tv("errors.saveFailed") }));
      return { ok: false, code: null };
    }
  };

  // The two kind-level slots (`productType: null`) — always present per the server's
  // fixed slot order. A product slot is a footnote under the marketplace row and is worth
  // showing only when its `ruleId` diverges from that row's — otherwise it is the SAME
  // rate the sales row already states, and four identical lines would be noise.
  const saasSlot = inForce?.find((r) => r.kind === "saas" && r.productType === null);
  const mktSlot = inForce?.find((r) => r.kind === "marketplace" && r.productType === null);
  const productSlots = (inForce || [])
    .filter((r) => r.kind === "marketplace" && r.productType !== null)
    .filter((r) => !mktSlot || r.ruleId !== mktSlot.ruleId);

  // `kindFilter` is a VIEW over the already-loaded history, not a second request: `rows`
  // stays unfiltered so `SourceChip` can always resolve a chip's rule regardless of which
  // kind the table happens to be showing.
  const historyRows = (rows || []).filter((r) => !kindFilter || r.kind === kindFilter);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sa-affiliate-rules">
      <div className="px-5 py-4 border-b border-slate-200">
        <h2 className="text-xl text-slate-800">{t("saff.rules.title")}</h2>
        <p className="text-sm text-slate-500 mt-0.5">{t("saff.rules.desc")}</p>
      </div>

      {loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
            onRetry={reload}
            retryLabel={tv("actions.retry")}
            testId="sa-affiliate-rules-load-error"
            retryTestId="sa-affiliate-rules-retry"
          />
        </div>
      ) : (
        <div data-testid="sa-rules-in-force" className="px-5 py-4 border-b border-slate-200 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">{t("saff.rules.inForceTitle")}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{t("saff.rules.inForceDesc")}</p>
          </div>
          <div className="space-y-4">
            {saasSlot && (
              <InForceMainRow
                slot={saasSlot}
                label={t("saff.rules.slot.saas")}
                baseText={t("saff.rules.base.saas")}
                rows={rows}
                lang={lang}
                t={t}
                rowTestId="sa-rate-in-force-saas"
                chipTestId="sa-rate-source-saas"
              />
            )}
            {mktSlot && (
              <div>
                <InForceMainRow
                  slot={mktSlot}
                  label={t("saff.rules.slot.marketplace")}
                  baseText={t("saff.rules.base.marketplace")}
                  rows={rows}
                  lang={lang}
                  t={t}
                  rowTestId="sa-rate-in-force-marketplace"
                  chipTestId="sa-rate-source-marketplace"
                />
                {productSlots.length > 0 && (
                  <div className="ml-6 mt-2 divide-y divide-slate-100">
                    {productSlots.map((p) => (
                      <InForceProductLine
                        key={p.productType}
                        slot={p}
                        rows={rows}
                        lang={lang}
                        t={t}
                        rowTestId={`sa-rate-in-force-marketplace-${p.productType}`}
                        chipTestId={`sa-rate-source-marketplace-${p.productType}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {canManage && (
      <div className="px-5 py-4 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700">{t("saff.rules.setTitle")}</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">{t("saff.rules.setDesc")}</p>
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div>
            <Label htmlFor="sa-rule-kind">{t("saff.rules.kind")}</Label>
            <select
              id="sa-rule-kind"
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              data-testid="sa-rule-kind"
              className="w-full mt-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm"
            >
              {AFFILIATE_KINDS.map((k) => <option key={k} value={k}>{t(KIND_KEYS[k])}</option>)}
            </select>
          </div>
          {draft.kind !== "saas" && (
            <div>
              <Label htmlFor="sa-rule-product-type">{t("saff.rules.productType")}</Label>
              <select
                id="sa-rule-product-type"
                value={draft.productType}
                onChange={(e) => setDraft({ ...draft, productType: e.target.value })}
                data-testid="sa-rule-product-type"
                className="w-full mt-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              >
                <option value={ANY_PRODUCT_TYPE}>{t("saff.rules.productTypeAny")}</option>
                {PRODUCT_TYPES.map((pt) => <option key={pt} value={pt}>{t(`gw.productType.${pt}`)}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label htmlFor="sa-rule-rate">{t("saff.rules.rate")}</Label>
            <Input
              id="sa-rule-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={draft.ratePct}
              onChange={(e) => setDraft({ ...draft, ratePct: e.target.value })}
              data-testid="sa-rule-rate"
              className="mt-1"
            />
            <p className="text-[11px] text-slate-500 mt-1" data-testid="sa-rule-rate-help">
              {rateValid ? t("saff.rules.rateHint", { bps }) : t("saff.rules.rateHelp")}
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="sa-rule-note">{t("saff.rules.note")}</Label>
            <Input
              id="sa-rule-note"
              value={draft.note}
              maxLength={MAX_NOTE}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              data-testid="sa-rule-note"
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-5 flex items-center justify-between gap-3">
            {refusal
              ? <p className="text-sm text-rose-700" role="alert" data-testid="sa-rule-error">{refusal}</p>
              : <span />}
            <Button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!valid}
              data-testid="sa-rule-submit"
              // Measured contrast, not taste: the shared `Button` default variant is
              // white-on-sage (2.22:1, fails WCAG AA). `bg-sol-gold text-sol-midnight` is
              // 10.26:1 and the CTA style the other 19 super-admin screens already use;
              // `Button` is kept for its size/focus-ring/disabled handling and this
              // className override wins because button.jsx merges classes with
              // tailwind-merge.
              className="bg-sol-gold text-sol-midnight hover:bg-sol-gold/90"
            >
              {t("saff.rules.add")}
            </Button>
          </div>
        </div>
      </div>
      )}

      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <span className="text-xs text-slate-500">{t("saff.rules.historyTitle")}</span>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          data-testid="sa-rules-kind-filter"
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="">{t("saff.rules.filterAllKinds")}</option>
          {AFFILIATE_KINDS.map((k) => <option key={k} value={k}>{t(KIND_KEYS[k])}</option>)}
        </select>
      </div>

      {loadError ? null : rows && historyRows.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-sm" data-testid="sa-affiliate-rules-empty">
          {t("saff.rules.emptyHistory")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">{t("saff.rules.colKind")}</th>
                <th className="text-left px-4 py-2">{t("saff.rules.colProductType")}</th>
                <th className="text-right px-4 py-2">{t("saff.rules.colRate")}</th>
                <th className="text-left px-4 py-2">{t("saff.rules.colFrom")}</th>
                <th className="text-left px-4 py-2">{t("saff.rules.colTo")}</th>
                <th className="text-left px-4 py-2">{t("saff.rules.colSetBy")}</th>
                <th className="text-left px-4 py-2">{t("saff.rules.colNote")}</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`sa-rule-${r.id}`}>
                  <td className="px-4 py-2 text-slate-700">{KIND_KEYS[r.kind] ? t(KIND_KEYS[r.kind]) : r.kind}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {r.productType ? t(`gw.productType.${r.productType}`) : t("saff.rules.productTypeAny")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                    {t("saff.rules.ratePct", { pct: fmtBps(r.rateBps) })}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{fmtDate(r.effectiveFrom, lang)}</td>
                  <td className="px-4 py-2">
                    {r.effectiveTo
                      ? <span className="text-slate-700">{fmtDate(r.effectiveTo, lang)}</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {t("saff.rules.openBadge")}
                        </span>}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{r.setBy || EMPTY}</td>
                  <td className="px-4 py-2 text-slate-700">{r.note || EMPTY}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && <StepUpConfirmDialog
        open={confirming}
        onClose={(didMutate) => { setConfirming(false); setRefusal(""); if (didMutate) reload(); }}
        titleKey="saff.rules.confirmTitle"
        descKey="saff.rules.confirmDesc"
        requireAck
        ackKey="saff.rules.ack"
        onConfirm={confirm}
        gatewayApi={gatewayApi}
      />}
    </section>
  );
}
